import type { ItemProps } from "@bedrock/domain";
import { Icon, IconButton } from "@bedrock/ui";
import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from "react";
import {
  clampSpreadsheetZoom,
  normalizeSpreadsheetRange,
  spreadsheetCellKey,
  spreadsheetMergeOwner,
  spreadsheetRangeToTsv,
  spreadsheetVisibleAxis,
  SPREADSHEET_CANVAS_LIMITS,
  SPREADSHEET_DETAIL_LIMITS,
  SPREADSHEET_MAX_PREVIEW_BYTES,
  SPREADSHEET_PARSE_TIMEOUT_MS,
  type SpreadsheetCell,
  type SpreadsheetCellStyle,
  type SpreadsheetSheetModel,
  type SpreadsheetWorkbookSummary,
  type SpreadsheetWorkerRequest,
  type SpreadsheetWorkerResponse
} from "./spreadsheet-model";

type SpreadsheetProps = Extract<ItemProps, { kind: "spreadsheet" }>;
type SpreadsheetVariant = "canvas" | "detail";
type ReaderStatus = "idle" | "loading" | "ready" | "error" | "too-large";
type CellPoint = { row: number; column: number };
type CellSelection = { anchor: CellPoint; focus: CellPoint };

interface WorkbookState {
  status: ReaderStatus;
  message: string;
  summary: SpreadsheetWorkbookSummary | null;
  sheet: SpreadsheetSheetModel | null;
}

const initialWorkbookState: WorkbookState = { status: "idle", message: "", summary: null, sheet: null };

export function SpreadsheetViewer({ props, variant, addedAt }: { props: SpreadsheetProps; variant: SpreadsheetVariant; addedAt?: number }) {
  const limits = variant === "canvas" ? SPREADSHEET_CANVAS_LIMITS : SPREADSHEET_DETAIL_LIMITS;
  const workerRef = useRef<Worker | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const requestRef = useRef(0);
  const [workbook, setWorkbook] = useState<WorkbookState>(initialWorkbookState);
  const [zoom, setZoom] = useState(1);
  const [selection, setSelection] = useState<CellSelection>({ anchor: { row: 0, column: 0 }, focus: { row: 0, column: 0 } });

  const clearTimeoutRef = useCallback(() => {
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const armTimeout = useCallback(() => {
    clearTimeoutRef();
    timeoutRef.current = window.setTimeout(() => {
      workerRef.current?.terminate();
      workerRef.current = null;
      setWorkbook((current) => ({ ...current, status: "error", message: "Preview exceeded the 15-second parsing limit. Download the workbook to open it externally." }));
    }, SPREADSHEET_PARSE_TIMEOUT_MS);
  }, [clearTimeoutRef]);

  useEffect(() => {
    let disposed = false;
    setWorkbook(props.localUrl ? { ...initialWorkbookState, status: "loading" } : initialWorkbookState);
    setSelection({ anchor: { row: 0, column: 0 }, focus: { row: 0, column: 0 } });
    setZoom(1);
    if (!props.localUrl) return;
    if (typeof Worker === "undefined") {
      setWorkbook({ status: "error", message: "Workbook preview workers are unavailable in this environment.", summary: null, sheet: null });
      return;
    }
    const worker = new Worker(new URL("./spreadsheet-parser.worker.ts", import.meta.url), { type: "module", name: "bedrock-spreadsheet-parser" });
    workerRef.current = worker;
    worker.addEventListener("message", (event: MessageEvent<SpreadsheetWorkerResponse>) => {
      if (disposed || event.data.requestId !== requestRef.current) return;
      clearTimeoutRef();
      const response = event.data;
      if (response.kind === "error") {
        setWorkbook((current) => ({ ...current, status: "error", message: response.message }));
        return;
      }
      if (response.kind === "loaded") setWorkbook({ status: "ready", message: "", summary: response.summary, sheet: response.sheet });
      if (response.kind === "sheet") setWorkbook((current) => ({ ...current, status: "ready", message: "", sheet: response.sheet }));
    });
    worker.addEventListener("error", () => {
      clearTimeoutRef();
      worker.terminate();
      if (workerRef.current === worker) workerRef.current = null;
      if (!disposed) setWorkbook((current) => ({ ...current, status: "error", message: "This workbook could not be read. Download it to open externally." }));
    });
    void fetch(props.localUrl).then((response) => response.blob()).then(async (blob) => {
      if (disposed) return;
      if (blob.size > SPREADSHEET_MAX_PREVIEW_BYTES) {
        worker.terminate();
        workerRef.current = null;
        setWorkbook({ status: "too-large", message: "This workbook is larger than the 50 MiB preview limit.", summary: null, sheet: null });
        return;
      }
      const bytes = await blob.arrayBuffer();
      if (disposed) return;
      const requestId = ++requestRef.current;
      armTimeout();
      const format = props.mimeType === "application/vnd.ms-excel" ? "xls" : "xlsx";
      worker.postMessage({ kind: "load", requestId, bytes, format, limits } satisfies SpreadsheetWorkerRequest, [bytes]);
    }).catch(() => {
      worker.terminate();
      if (workerRef.current === worker) workerRef.current = null;
      if (!disposed) setWorkbook({ status: "error", message: "This workbook is not available for preview.", summary: null, sheet: null });
    });
    return () => {
      disposed = true;
      clearTimeoutRef();
      worker.postMessage({ kind: "cancel", requestId: requestRef.current } satisfies SpreadsheetWorkerRequest);
      worker.postMessage({ kind: "dispose" } satisfies SpreadsheetWorkerRequest);
      worker.terminate();
      if (workerRef.current === worker) workerRef.current = null;
    };
  }, [armTimeout, clearTimeoutRef, limits, props.localUrl, props.mimeType]);

  const selectSheet = (sheetName: string) => {
    const worker = workerRef.current;
    if (!worker || workbook.sheet?.name === sheetName) return;
    worker.postMessage({ kind: "cancel", requestId: requestRef.current } satisfies SpreadsheetWorkerRequest);
    const requestId = ++requestRef.current;
    setWorkbook((current) => ({ ...current, status: "loading" }));
    setSelection({ anchor: { row: 0, column: 0 }, focus: { row: 0, column: 0 } });
    armTimeout();
    worker.postMessage({ kind: "sheet", requestId, sheetName, limits } satisfies SpreadsheetWorkerRequest);
  };

  const format = props.mimeType === "application/vnd.ms-excel" ? "XLS" : "XLSX";
  const selectedOwner = workbook.sheet ? spreadsheetMergeOwner(workbook.sheet, selection.focus) : selection.focus;
  const selectedCell = workbook.sheet?.cells[spreadsheetCellKey(selectedOwner.row, selectedOwner.column)];
  const selectedAddress = selectedCell?.address
    ?? `${workbook.sheet?.columns[selection.focus.column]?.label ?? "A"}${(workbook.sheet?.rows[selection.focus.row]?.index ?? 0) + 1}`;
  const limited = workbook.summary?.truncatedSheets || workbook.sheet?.truncatedRows || workbook.sheet?.truncatedColumns || workbook.sheet?.truncatedCells;
  const limitDescription = workbook.summary && workbook.sheet ? [
    workbook.summary.truncatedSheets ? `first ${limits.sheetLimit} visible sheets` : "",
    workbook.sheet.truncatedRows ? `first ${limits.rowLimit.toLocaleString()} rows` : "",
    workbook.sheet.truncatedColumns ? `first ${limits.columnLimit.toLocaleString()} columns` : "",
    workbook.sheet.truncatedCells ? `first ${limits.cellLimit.toLocaleString()} populated cells` : ""
  ].filter(Boolean).join(" · ") : "";

  return <section className={`spreadsheet-reader spreadsheet-reader--${variant}`} aria-label={`${props.filename} workbook viewer`}>
    {variant === "detail" && <>
      <header className="spreadsheet-reader__header">
        <div className="spreadsheet-reader__identity" title={props.filename}><span><Icon name="spreadsheet"/></span><div><strong>{props.filename}</strong><small>{format} · {workbook.summary ? `${workbook.summary.totalVisibleSheets} ${workbook.summary.totalVisibleSheets === 1 ? "sheet" : "sheets"}` : "Workbook"}{addedAt ? ` · Added ${new Date(addedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}` : ""}{limited ? " · bounded preview" : ""}</small></div></div>
        <div className="spreadsheet-reader__zoom" aria-label="Workbook zoom controls">
          <IconButton label="Zoom out" tooltipPlacement="bottom" icon="zoom-out" disabled={zoom <= .75} onClick={() => setZoom((value) => clampSpreadsheetZoom(value - .25))}/>
          <button type="button" onClick={() => setZoom(1)} aria-label="Reset workbook zoom to 100%">{Math.round(zoom * 100)}%</button>
          <IconButton label="Zoom in" tooltipPlacement="bottom" icon="zoom-in" disabled={zoom >= 1.5} onClick={() => setZoom((value) => clampSpreadsheetZoom(value + .25))}/>
        </div>
      </header>
      <div className="spreadsheet-reader__formula" aria-live="polite"><span>{selectedAddress}</span><output>{selectedCell?.formula ? `=${selectedCell.formula}` : selectedCell?.display || selectedCell?.raw || "Select a cell to inspect its value"}</output></div>
    </>}
    <div className="spreadsheet-reader__body">
      <SpreadsheetReaderState status={workbook.status} message={workbook.message} hasSource={Boolean(props.localUrl)}/>
      {variant === "detail" && limitDescription && <div className="spreadsheet-reader__limit" role="status">Preview limited to {limitDescription}</div>}
      {workbook.sheet && <SpreadsheetGrid sheet={workbook.sheet} variant={variant} zoom={variant === "canvas" ? 1 : zoom} selection={selection} onSelectionChange={setSelection}/>}
    </div>
    {variant === "canvas" && workbook.summary && <span className="spreadsheet-reader__canvas-count">{workbook.summary.totalVisibleSheets} {workbook.summary.totalVisibleSheets === 1 ? "sheet" : "sheets"}</span>}
    {variant === "detail" && workbook.summary && <footer className="spreadsheet-reader__tabs" aria-label="Workbook sheets">
      <span aria-hidden="true"><Icon name="spreadsheet"/></span>
      <div>{workbook.summary.sheetNames.map((name) => <button key={name} type="button" className={workbook.sheet?.name === name ? "is-active" : ""} aria-pressed={workbook.sheet?.name === name} onClick={() => selectSheet(name)}>{name}</button>)}</div>
      {limited && <small title={`Preview limited to ${limitDescription}`}>Preview limited</small>}
    </footer>}
  </section>;
}

function SpreadsheetReaderState({ status, message, hasSource }: { status: ReaderStatus; message: string; hasSource: boolean }) {
  if (status === "ready") return null;
  if (status === "loading") return <div className="spreadsheet-reader__loading" aria-label="Loading workbook preview">{Array.from({ length: 30 }, (_, index) => <i key={index}/>)}</div>;
  return <div className="spreadsheet-reader__state" role={status === "error" ? "alert" : undefined}><Icon name="spreadsheet"/><strong>Preview unavailable</strong><span>{message || (hasSource ? "This workbook could not be previewed." : "This workbook is not available offline yet.")}</span></div>;
}

function SpreadsheetGrid({ sheet, variant, zoom, selection, onSelectionChange }: { sheet: SpreadsheetSheetModel; variant: SpreadsheetVariant; zoom: number; selection: CellSelection; onSelectionChange: (selection: CellSelection) => void }) {
  const gridId = useId().replace(/:/g, "");
  const stageRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [viewport, setViewport] = useState({ scrollLeft: 0, scrollTop: 0, width: 900, height: 600 });
  const rowHeaderWidth = variant === "canvas" ? 28 : 48;
  const columnHeaderHeight = variant === "canvas" ? 24 : 30;
  const rowLayout = useMemo(() => axisLayout(sheet.rows.map((row) => row.size * zoom)), [sheet.rows, zoom]);
  const columnLayout = useMemo(() => axisLayout(sheet.columns.map((column) => column.size * zoom)), [sheet.columns, zoom]);
  const normalizedSelection = normalizeSpreadsheetRange(selection.anchor, selection.focus);
  const activeOwner = spreadsheetMergeOwner(sheet, selection.focus);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const measure = () => setViewport((current) => ({ ...current, width: stage.clientWidth, height: stage.clientHeight }));
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(stage);
    const stopDragging = () => { dragging.current = false; };
    window.addEventListener("pointerup", stopDragging);
    return () => { observer?.disconnect(); window.removeEventListener("pointerup", stopDragging); };
  }, []);

  const visibleRows = variant === "canvas" ? indexRange(0, sheet.rows.length - 1) : spreadsheetVisibleAxis(rowLayout.offsets, rowLayout.sizes, viewport.scrollTop - columnHeaderHeight, viewport.height, 2);
  const visibleColumns = variant === "canvas" ? indexRange(0, sheet.columns.length - 1) : spreadsheetVisibleAxis(columnLayout.offsets, columnLayout.sizes, viewport.scrollLeft - rowHeaderWidth, viewport.width, 2);
  const coordinates = new Map<string, CellPoint>();
  for (const row of visibleRows) for (const column of visibleColumns) coordinates.set(spreadsheetCellKey(row, column), { row, column });
  for (const merge of sheet.merges) {
    if (merge.endRow < (visibleRows[0] ?? 0) || merge.startRow > (visibleRows.at(-1) ?? -1) || merge.endColumn < (visibleColumns[0] ?? 0) || merge.startColumn > (visibleColumns.at(-1) ?? -1)) continue;
    coordinates.set(spreadsheetCellKey(merge.startRow, merge.startColumn), { row: merge.startRow, column: merge.startColumn });
  }

  const choose = (point: CellPoint, extend: boolean) => {
    const owner = spreadsheetMergeOwner(sheet, point);
    onSelectionChange(extend ? { anchor: selection.anchor, focus: owner } : { anchor: owner, focus: owner });
  };
  const onPointerDown = (event: PointerEvent<HTMLDivElement>, point: CellPoint) => {
    if (variant !== "detail") return;
    event.preventDefault();
    dragging.current = true;
    choose(point, event.shiftKey);
    stageRef.current?.focus();
  };
  const onPointerEnter = (point: CellPoint) => {
    if (variant === "detail" && dragging.current) onSelectionChange({ anchor: selection.anchor, focus: spreadsheetMergeOwner(sheet, point) });
  };
  const moveFocus = (row: number, column: number, extend: boolean) => {
    const next = spreadsheetMergeOwner(sheet, { row: clamp(row, 0, sheet.rows.length - 1), column: clamp(column, 0, sheet.columns.length - 1) });
    onSelectionChange(extend ? { anchor: selection.anchor, focus: next } : { anchor: next, focus: next });
    scrollPointIntoView(stageRef.current, rowLayout, columnLayout, next, rowHeaderWidth, columnHeaderHeight);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (variant !== "detail") return;
    const command = event.ctrlKey || event.metaKey;
    if (command && event.key.toLocaleLowerCase() === "a") {
      event.preventDefault();
      onSelectionChange({ anchor: { row: 0, column: 0 }, focus: { row: sheet.rows.length - 1, column: sheet.columns.length - 1 } });
      return;
    }
    if (command && event.key.toLocaleLowerCase() === "c") {
      event.preventDefault();
      void copyText(spreadsheetRangeToTsv(sheet, normalizedSelection));
      return;
    }
    if (event.key === "PageUp" || event.key === "PageDown") {
      event.preventDefault();
      stageRef.current?.scrollBy({ top: (event.key === "PageUp" ? -1 : 1) * Math.max(120, viewport.height - columnHeaderHeight - 24) });
      return;
    }
    const delta = event.key === "ArrowUp" ? [-1, 0] : event.key === "ArrowDown" ? [1, 0] : event.key === "ArrowLeft" ? [0, -1] : event.key === "ArrowRight" ? [0, 1] : null;
    if (delta) {
      event.preventDefault();
      moveFocus(selection.focus.row + delta[0]!, selection.focus.column + delta[1]!, event.shiftKey);
      return;
    }
    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      moveFocus(selection.focus.row, event.key === "Home" ? 0 : sheet.columns.length - 1, event.shiftKey);
    }
  };

  const selectionStyle: CSSProperties = {
    left: rowHeaderWidth + columnLayout.offsets[normalizedSelection.startColumn]!,
    top: columnHeaderHeight + rowLayout.offsets[normalizedSelection.startRow]!,
    width: spanSize(columnLayout, normalizedSelection.startColumn, normalizedSelection.endColumn),
    height: spanSize(rowLayout, normalizedSelection.startRow, normalizedSelection.endRow)
  };

  return <div
    ref={stageRef}
    className="spreadsheet-grid"
    role="grid"
    aria-label={`${sheet.name} worksheet`}
    aria-rowcount={sheet.rows.length}
    aria-colcount={sheet.columns.length}
    aria-activedescendant={variant === "detail" ? `${gridId}-${spreadsheetCellKey(activeOwner.row, activeOwner.column)}` : undefined}
    tabIndex={variant === "detail" ? 0 : -1}
    onKeyDown={onKeyDown}
    onScroll={(event) => setViewport({ scrollLeft: event.currentTarget.scrollLeft, scrollTop: event.currentTarget.scrollTop, width: event.currentTarget.clientWidth, height: event.currentTarget.clientHeight })}
  >
    <div className="spreadsheet-grid__surface" style={{ width: rowHeaderWidth + columnLayout.total, height: columnHeaderHeight + rowLayout.total }}>
      {visibleColumns.map((column) => <div key={`column-${column}`} className="spreadsheet-grid__column-label" role="columnheader" style={{ left: rowHeaderWidth + columnLayout.offsets[column]!, top: viewport.scrollTop, width: columnLayout.sizes[column]!, height: columnHeaderHeight }}>{sheet.columns[column]?.label}</div>)}
      {visibleRows.map((row) => <div key={`row-${row}`} className="spreadsheet-grid__row-label" role="rowheader" style={{ left: viewport.scrollLeft, top: columnHeaderHeight + rowLayout.offsets[row]!, width: rowHeaderWidth, height: rowLayout.sizes[row]! }}>{sheet.rows[row]?.label}</div>)}
      {[...coordinates.values()].map((point) => {
        if (isCoveredMerge(sheet, point)) return null;
        const cell = sheet.cells[spreadsheetCellKey(point.row, point.column)];
        const width = spanSize(columnLayout, point.column, point.column + (cell?.columnSpan ?? 1) - 1);
        const height = spanSize(rowLayout, point.row, point.row + (cell?.rowSpan ?? 1) - 1);
        return <div
          key={spreadsheetCellKey(point.row, point.column)}
          id={`${gridId}-${spreadsheetCellKey(point.row, point.column)}`}
          className="spreadsheet-grid__cell"
          role="gridcell"
          aria-rowindex={point.row + 1}
          aria-colindex={point.column + 1}
          aria-label={cellAriaLabel(sheet, point, cell)}
          style={{ left: rowHeaderWidth + columnLayout.offsets[point.column]!, top: columnHeaderHeight + rowLayout.offsets[point.row]!, width, height, fontSize: Math.max(7, 12 * zoom), ...cellCss(cell?.style) }}
          onPointerDown={(event) => onPointerDown(event, point)}
          onPointerEnter={() => onPointerEnter(point)}
        >{cell?.display ?? ""}</div>;
      })}
      {variant === "detail" && <div className="spreadsheet-grid__selection" aria-hidden="true" style={selectionStyle}/>}
      <div className="spreadsheet-grid__corner" aria-hidden="true" style={{ left: viewport.scrollLeft, top: viewport.scrollTop, width: rowHeaderWidth, height: columnHeaderHeight }}><i/></div>
    </div>
  </div>;
}

interface AxisLayout { offsets: number[]; sizes: number[]; total: number }

function axisLayout(sizes: number[]): AxisLayout {
  const offsets: number[] = [];
  let total = 0;
  for (const size of sizes) { offsets.push(total); total += size; }
  return { offsets, sizes, total };
}

function indexRange(start: number, end: number): number[] {
  return end < start ? [] : Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

function spanSize(layout: AxisLayout, start: number, end: number): number {
  const boundedEnd = Math.min(layout.sizes.length - 1, end);
  return layout.offsets[boundedEnd]! + layout.sizes[boundedEnd]! - layout.offsets[start]!;
}

function isCoveredMerge(sheet: SpreadsheetSheetModel, point: CellPoint): boolean {
  return sheet.merges.some((merge) => point.row >= merge.startRow && point.row <= merge.endRow && point.column >= merge.startColumn && point.column <= merge.endColumn && (point.row !== merge.startRow || point.column !== merge.startColumn));
}

function cellAriaLabel(sheet: SpreadsheetSheetModel, point: CellPoint, cell: SpreadsheetCell | undefined): string {
  const column = sheet.columns[point.column]?.label ?? "A";
  const row = (sheet.rows[point.row]?.index ?? point.row) + 1;
  return [cell?.address ?? `${column}${row}`, cell?.display].filter(Boolean).join(", ");
}

function cellCss(style: SpreadsheetCellStyle | undefined): CSSProperties {
  if (!style) return {};
  return {
    ...(style.background ? { backgroundColor: style.background } : {}),
    ...(style.color ? { color: style.color } : {}),
    ...(style.bold ? { fontWeight: 700 } : {}),
    ...(style.italic ? { fontStyle: "italic" } : {}),
    ...(style.underline ? { textDecoration: "underline" } : {}),
    ...(style.align ? { justifyContent: style.align === "left" ? "flex-start" : style.align === "right" ? "flex-end" : "center", textAlign: style.align } : {}),
    ...(style.verticalAlign ? { alignItems: style.verticalAlign === "top" ? "flex-start" : style.verticalAlign === "bottom" ? "flex-end" : "center" } : {}),
    ...(style.wrap ? { whiteSpace: "normal", overflowWrap: "anywhere" } : {}),
    ...(style.borders?.top ? { borderTopColor: style.borders.top } : {}),
    ...(style.borders?.right ? { borderRightColor: style.borders.right } : {}),
    ...(style.borders?.bottom ? { borderBottomColor: style.borders.bottom } : {}),
    ...(style.borders?.left ? { borderLeftColor: style.borders.left } : {})
  };
}

function scrollPointIntoView(stage: HTMLDivElement | null, rows: AxisLayout, columns: AxisLayout, point: CellPoint, rowHeaderWidth: number, columnHeaderHeight: number): void {
  if (!stage) return;
  const left = rowHeaderWidth + columns.offsets[point.column]!;
  const right = left + columns.sizes[point.column]!;
  const top = columnHeaderHeight + rows.offsets[point.row]!;
  const bottom = top + rows.sizes[point.row]!;
  if (left < stage.scrollLeft + rowHeaderWidth) stage.scrollLeft = Math.max(0, left - rowHeaderWidth);
  else if (right > stage.scrollLeft + stage.clientWidth) stage.scrollLeft = right - stage.clientWidth;
  if (top < stage.scrollTop + columnHeaderHeight) stage.scrollTop = Math.max(0, top - columnHeaderHeight);
  else if (bottom > stage.scrollTop + stage.clientHeight) stage.scrollTop = bottom - stage.clientHeight;
}

async function copyText(value: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.append(textarea);
    textarea.select();
    document.execCommand("copy");
    textarea.remove();
  }
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
